import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hexagon } from "lucide-react";
import { toast } from "sonner";
import PasswordStrength, { evaluatePassword } from "@/components/PasswordStrength";
import { useBotGuard } from "@/lib/botGuard";

export default function Register() {
  const { register } = useAuth();
  const { withGuard, Honeypot } = useBotGuard();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!evaluatePassword(password).meetsPolicy) {
      toast.error("Password doesn't meet the minimum requirements");
      return;
    }
    setLoading(true);
    try {
      const g = withGuard();
      await register(email, password, name, false, { website: g.website, form_ticket: g.form_ticket });
      toast.success("Welcome to Hive @ Buzzzillion");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <div className="w-9 h-9 rounded-xl bg-yellow-400 grid place-items-center">
            <Hexagon className="w-5 h-5 text-amber-900" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">Hive <span className="text-yellow-500">@</span> Buzzzillion</span>
        </Link>
        <div className="bg-white rounded-3xl border border-stone-200 p-8">
          <h1 className="font-display text-3xl font-medium">Start your journal</h1>
          <p className="text-stone-500 text-sm mt-1">Free. Your bees will thank you.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <Honeypot />
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" required maxLength={200} value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={8} maxLength={200} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="register-password-input" />
              <PasswordStrength password={password} testidPrefix="register-password-strength" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="register-submit-button">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-stone-500 mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-amber-700 font-medium hover:underline" data-testid="link-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
